import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Summary, SummarySkeleton } from "@/components/questions/results";
import { ErrorWithRetry } from "@/components/ui";
import API from "@/hooks/api";

import type { County2024 } from "./model";
import { readSummary, writeSummary } from "./storage";
import { NATIONAL_QUESTIONS, nationalFigures } from "./summary";
import type {
  ModelSettings,
  Office,
  PublishedSummary,
  StateSample,
} from "./types";

interface NationalSummaryProps {
  county2024: County2024 | null;
  /** `turnoutEdge` of the samples. */
  edge: number;
  /** `nationalEnvironment` of the samples at these settings. */
  environment: number | null;
  office: Office;
  /** While the surveys or the 2024 results load, only a summary saved in
      this browser is shown and nothing is requested. */
  paused: boolean;
  samples: Record<string, StateSample>;
  settings: ModelSettings;
}

/** AI key insights on an office's national picture, written from
    Foresight's figures and saved in this browser per question and figures. */
const NationalSummary: FC<NationalSummaryProps> = ({
  county2024,
  edge,
  environment,
  office,
  paused,
  samples,
  settings,
}) => {
  const { error, generateSummaries } = API.Midterms.useGenerateSummaries();
  const figures = useMemo(
    () =>
      nationalFigures({
        county2024,
        edge,
        environment,
        office,
        samples,
        settings,
      }),
    [county2024, edge, environment, office, samples, settings]
  );
  const question = NATIONAL_QUESTIONS[office];
  const saved = useMemo(
    () => (figures === null ? null : readSummary(question, figures)),
    [figures, question]
  );
  // By question and figures; null when the request failed.
  const [results, setResults] = useState<
    Partial<Record<string, PublishedSummary | null>>
  >({});
  const requested = useRef(new Set<string>());

  const generate = useCallback(
    async (context: string) => {
      const id = `${question}\n${context}`;
      requested.current.add(id);
      const summaries = await generateSummaries([
        { context, key: office, question },
      ]);
      const found = summaries?.find((s) => s.key === office);
      const summary = found
        ? { headline: found.headline, summary: found.summary }
        : null;
      if (summary && summary.summary !== null) {
        writeSummary(question, context, summary);
      }
      setResults((current) => ({ ...current, [id]: summary }));
    },
    [generateSummaries, office, question]
  );

  useEffect(() => {
    if (paused || figures === null || saved) return;
    if (requested.current.has(`${question}\n${figures}`)) return;
    // Deferred so the hook's state updates don't run synchronously inside
    // the effect.
    const timeout = setTimeout(() => void generate(figures), 0);
    return () => clearTimeout(timeout);
  }, [figures, generate, paused, question, saved]);

  if (figures === null) return null;

  const id = `${question}\n${figures}`;
  const retry = () => {
    setResults((current) => ({ ...current, [id]: undefined }));
    void generate(figures);
  };

  const result = results[id] === undefined ? (saved ?? undefined) : results[id];
  if (result === undefined) return <SummarySkeleton generating={!paused} />;
  if (result === null || result.summary === null) {
    return (
      <ErrorWithRetry
        error={
          result === null
            ? (error ?? "The request failed.")
            : "The model returned no summary."
        }
        onRetry={retry}
        title="Couldn't write the key insights"
      />
    );
  }
  return <Summary summary={{ ...result, visualization: null }} />;
};

export default NationalSummary;
